import { SupplierUserRole } from '@choco-admin/types';
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useSupplierAddUserMutation,
  useSupplierRemoveUserMutation,
  useUsersPageUpdateSupplierUserRolesMutation,
} from '../../../generated';
import { supplierUserRoleToGqlRole } from '../../../utils/supplierRole';
import { SupplierSelectOption } from '../components/SupplierLinking/utils';

export interface UseSupplierLinkingConfig {
  /** Called after a successful link/unlink to refresh data; replaces page reload. */
  onRefetch?: () => Promise<void>;
}

export interface UseSupplierLinkingResponse {
  linkUserToSupplier: (
    supplier: SupplierSelectOption,
    userId: string
  ) => Promise<void>;
  unlinkUserFromSupplier: (userId: string) => Promise<void>;
  updateSupplierRole: (userId: string, role: SupplierUserRole) => Promise<void>;
  isLinking: boolean;
  isUnlinking: boolean;
  isUpdatingRole: boolean;
}

export const useSupplierLinking = (
  config: UseSupplierLinkingConfig = {}
): UseSupplierLinkingResponse => {
  const { onRefetch } = config;
  const { addNotification } = useNotification();

  const [supplierAddUser, { loading: isLinking }] =
    useSupplierAddUserMutation();
  const [supplierRemoveUser, { loading: isUnlinking }] =
    useSupplierRemoveUserMutation();
  const [updateSupplierUserRoles, { loading: isUpdatingRole }] =
    useUsersPageUpdateSupplierUserRolesMutation();

  const linkUserToSupplier = useCallback(
    async (supplier: SupplierSelectOption, userId: string) => {
      try {
        await supplierAddUser({
          variables: {
            userId,
            supplierId: supplier.value,
            withRoles: true,
          },
        });

        addNotification({
          type: 'success',
          title: 'User Linked',
          message: `Successfully linked user to ${supplier.label}`,
        });
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Link Failed',
          message: 'Failed to link user to supplier. Please try again.',
        });
        throw error;
      }

      // Refetch is best-effort: the mutation already succeeded, so a refetch
      // failure (e.g. transient network) must not be reported as a link
      // failure or block the caller from clearing local state.
      await onRefetch?.().catch(() => undefined);
    },
    [supplierAddUser, addNotification, onRefetch]
  );

  const unlinkUserFromSupplier = useCallback(
    async (userId: string) => {
      try {
        // userUpdate(supplierId: null) already clears the user's supplier
        // role(s) atomically as part of the same backend transaction (see
        // handleLinkSupplier in userService). Do NOT also call userRemoveRole
        // beforehand — splitting one atomic operation into two risks
        // orphaning the user (role stripped, still linked) if the second
        // call fails.
        await supplierRemoveUser({
          variables: {
            userId,
            withRoles: true,
          },
        });

        addNotification({
          type: 'success',
          title: 'User Unlinked',
          message: 'Successfully unlinked user from supplier',
        });
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Unlink Failed',
          message: 'Failed to unlink user from supplier. Please try again.',
        });
        throw error;
      }

      // Refetch is best-effort — see linkUserToSupplier.
      await onRefetch?.().catch(() => undefined);
    },
    [supplierRemoveUser, addNotification, onRefetch]
  );

  const updateSupplierRole = useCallback(
    async (userId: string, role: SupplierUserRole) => {
      try {
        // rolesToAdd OVERRIDES existing roles — a supplier user holds exactly
        // one — so always send a single-element array.
        await updateSupplierUserRoles({
          variables: {
            userId,
            rolesToAdd: [supplierUserRoleToGqlRole(role)],
          },
        });

        addNotification({
          type: 'success',
          title: 'Role Updated',
          message: 'Successfully updated the distributor role.',
        });
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Update Failed',
          message: 'Failed to update the distributor role. Please try again.',
        });
        throw error;
      }

      // Refetch is best-effort — see linkUserToSupplier.
      await onRefetch?.().catch(() => undefined);
    },
    [updateSupplierUserRoles, addNotification, onRefetch]
  );

  return {
    linkUserToSupplier,
    unlinkUserFromSupplier,
    updateSupplierRole,
    isLinking,
    isUnlinking,
    isUpdatingRole,
  };
};
